import { z } from 'zod'

/**
 * How much the server trusts a presence row right now. Derived server-side so both
 * clients agree and so crowd corroboration — invisible to a client — counts:
 * - `live`: self-confirmed recently, or exit-anchored with no exit fired
 * - `corroborated`: other devices are confirming this exact place right now
 * - `fading`: neither; render the last confirmation time and sink the row
 */
export const presenceStateValues = ['live', 'corroborated', 'fading'] as const
export type PresenceState = (typeof presenceStateValues)[number]

export const presenceVisibleRowSchema = z.object({
  placeId: z.string().uuid(),
  placeName: z.string(),
  placeType: z.enum(['shared', 'public', 'event']),
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  presentSince: z.string(),
  // Optional so a not-yet-deployed backend (no column) still parses; the UI
  // treats a missing value as "active now" rather than rendering "last seen NaN".
  lastSeenAt: z.string().optional(),
  /** A one-tap intent the present user dropped over their face (🍻 / ⚽ / 🎮 …). */
  statusEmoji: z.string().nullable().optional(),
  /**
   * Presence is anchored by a background-capable geofence: the device will fire an
   * exit even with the app killed, so absence of heartbeats is not evidence of
   * absence — viewers keep the row live until exit or safety expiry instead of
   * aging it to "last seen".
   */
  bgAnchored: z.boolean().optional(),
  /** Event places: viewer and this person share a plan linked to the event (may not be friends). */
  viaPlan: z.boolean().optional(),
  /** Optional so a not-yet-deployed backend still parses; a missing value reads as `live`. */
  presenceState: z.enum(presenceStateValues).optional(),
  /** Start of the continuous stay — survives a TTL bounce or a short step outside. */
  sinceAt: z.string().optional(),
  /** Last self-confirming heartbeat; what "zuletzt 21:40" renders. */
  lastConfirmedAt: z.string().optional(),
  /** Everyone currently present at this row's place, viewer included (anonymous count).
   * Optional so a not-yet-deployed backend still parses. */
  presentTotal: z.number().int().nonnegative().optional(),
})
export type PresenceVisibleRow = z.infer<typeof presenceVisibleRowSchema>

export const presenceVisibleResponseSchema = z.object({
  rows: z.array(presenceVisibleRowSchema),
  /** The viewer's own presence rows folded in so one call answers both "who can I
   * see" and "where am I visible". Optional so a not-yet-deployed backend still parses. */
  me: z
    .array(
      z.object({
        placeId: z.string().uuid(),
        presentSince: z.string(),
        expiresAt: z.string(),
        bgAnchored: z.boolean(),
        presentTotal: z.number().int().nonnegative().optional(),
        heldUntil: z.string().nullable().optional(),
      }),
    )
    .optional(),
})
export type PresenceVisibleResponse = z.infer<typeof presenceVisibleResponseSchema>

/**
 * A self-reported "I'm here" that pins the presence row for a fixed duration: no GPS
 * check on the way in, no TTL expiry and no verify push until `heldUntil`. Any explicit
 * exit (geofence exit, out-of-range beat, manual leave, ghost mode) still ends it.
 */
export const presenceHoldDurationValues = ['2h', '4h', '8h'] as const
export type PresenceHoldDuration = (typeof presenceHoldDurationValues)[number]
export const presenceHoldSchema = z.object({ duration: z.enum(presenceHoldDurationValues) })
export type PresenceHoldInput = z.infer<typeof presenceHoldSchema>
export const presenceHoldResponseSchema = z.object({ heldUntil: z.string() })
export type PresenceHoldResponse = z.infer<typeof presenceHoldResponseSchema>

export const presenceHeartbeatSchema = z.object({
  placeId: z.string().uuid(),
  // Optional because a headless geofence ENTER can fire with no GPS fix available;
  // when present, the server enforces the distance guard against the place geometry.
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  accuracyM: z.number().nonnegative().optional(),
  /** Null clears it; omitted leaves the stored value untouched. */
  statusEmoji: z.string().max(16).nullable().optional(),
  /** Device has background location + armed geofences; omitted keeps the stored value. */
  bgAnchored: z.boolean().optional(),
  /** Background location permission is granted even though the row is not exit-anchored
   * (Android): background beats can refresh the row, so it earns a longer TTL. */
  bgPermission: z.boolean().optional(),
})
export type PresenceHeartbeatInput = z.infer<typeof presenceHeartbeatSchema>

/** `firstArrival` is true only on the beat that creates presence at an otherwise-empty
 * spot — it drives the "you're the first one here, ping the group?" local prompt. */
export const presenceHeartbeatResponseSchema = z.object({ firstArrival: z.boolean() })
export type PresenceHeartbeatResponse = z.infer<typeof presenceHeartbeatResponseSchema>

export const userPresenceSettingsSchema = z.object({
  sharePresenceByDefault: z.boolean(),
  ghostMode: z.boolean(),
  /** When ghost mode switches itself off again; null while it stays on until switched off. */
  ghostUntil: z.string().nullable(),
  /** Hi and compliments in Live Rooms, the default for every room. */
  acceptsHi: z.boolean(),
})
export type UserPresenceSettings = z.infer<typeof userPresenceSettingsSchema>

export const updatePresenceSettingsSchema = z.object({
  sharePresenceByDefault: z.boolean().optional(),
  ghostMode: z.boolean().optional(),
  /** With ghostMode true: hide until then instead of until switched off. */
  ghostUntil: z.string().datetime().nullable().optional(),
  acceptsHi: z.boolean().optional(),
})
export type UpdatePresenceSettingsInput = z.infer<typeof updatePresenceSettingsSchema>

export const presenceSettingsResponseSchema = z.object({ settings: userPresenceSettingsSchema })

export const eventLivePersonSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  statusEmoji: z.string().nullable().optional(),
  lastSeenAt: z.string().optional(),
  bgAnchored: z.boolean().optional(),
  presenceState: z.enum(presenceStateValues).optional(),
  sinceAt: z.string().optional(),
  lastConfirmedAt: z.string().optional(),
})
export type EventLivePerson = z.infer<typeof eventLivePersonSchema>

/**
 * Live roster for an event. `live` holds only while the event is running;
 * `placeId` is set as soon as the event's place takes presence, which opens
 * before the start. `publicPeople` is null unless the viewer is
 * currently public at this event (symmetric opt-in); it never contains
 * friends, ghosts, blocked users, or minors, and is capped server-side.
 */
export const eventLiveResponseSchema = z.object({
  live: z.boolean(),
  placeId: z.string().uuid().nullable(),
  total: z.number().int().nonnegative(),
  friends: z.array(eventLivePersonSchema),
  publicPeople: z.array(eventLivePersonSchema).nullable(),
  viewer: z.object({
    present: z.boolean(),
    isPublic: z.boolean(),
  }),
})
export type EventLiveResponse = z.infer<typeof eventLiveResponseSchema>

/**
 * `manual=true` marks a deliberate "Ich bin weg": it ends the stay, so a later return
 * counts as a fresh arrival again. A geofence-driven exit omits it and leaves the stay
 * resumable, which is what keeps a boundary flap from re-announcing anyone.
 */
export const presenceExitQuerySchema = z.object({ manual: z.literal('true').optional() })

export const presencePublicToggleSchema = z.object({ isPublic: z.boolean() })
export type PresencePublicToggleInput = z.infer<typeof presencePublicToggleSchema>

/**
 * My own presence rows. `/presence/visible` deliberately excludes the viewer, so
 * a client that reloads has no other way to learn it is already visible
 * somewhere — which is how a "become visible" button ends up offered to someone
 * who already is.
 */
export const myPresenceRowSchema = z.object({
  placeId: z.string().uuid(),
  presentSince: z.string(),
  expiresAt: z.string(),
  bgAnchored: z.boolean(),
  heldUntil: z.string().nullable().optional(),
})
export type MyPresenceRow = z.infer<typeof myPresenceRowSchema>

export const myPresenceResponseSchema = z.object({ rows: z.array(myPresenceRowSchema) })
export type MyPresenceResponse = z.infer<typeof myPresenceResponseSchema>
